/* wm-thumbnail-arrows.js
   Part of the WM Section Library.

   Adds prev/next arrows that scroll the product media gallery's THUMBNAIL RAIL.
   They do not change the selected slide - clicking a thumbnail still does that.

   Injects two buttons as siblings of .slideshow-controls__thumbnails-container
   inside <slideshow-controls thumbnails>, so they inherit that element's existing
   flex direction: row for the bottom rail, column for the left/right rail.

   Scroll axis is read from live geometry rather than the scroll-mode attribute,
   because the vertical rail only applies at >=750px.

   No Custom Element is defined: <slideshow-controls> is Horizon's own element and
   must not be re-registered. Init is idempotent and re-runs on section load and on
   DOM replacement (variant change re-renders the gallery). */

(() => {
  const ROOT_SELECTOR = 'media-gallery slideshow-controls[thumbnails]';
  const RAIL_SELECTOR = '.slideshow-controls__thumbnails-container';
  const ARROW_CLASS = 'wm-thumb-arrow';
  const INIT_ATTR = 'data-wm-thumb-arrows';

  const CHEVRON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7"/></svg>`;

  const config = document.querySelector('script[data-wm-thumbnail-arrows]');
  const LABELS = {
    prev: config?.dataset.labelPrevious || 'Previous',
    next: config?.dataset.labelNext || 'Next',
  };

  /** Live scroll axis of the rail. Falls back to horizontal when nothing overflows. */
  const axisOf = (rail) => (rail.scrollHeight - rail.clientHeight > 1 ? 'vertical' : 'horizontal');

  const metrics = (rail) => {
    const vertical = axisOf(rail) === 'vertical';
    return {
      vertical,
      position: vertical ? rail.scrollTop : rail.scrollLeft,
      viewport: vertical ? rail.clientHeight : rail.clientWidth,
      total: vertical ? rail.scrollHeight : rail.scrollWidth,
    };
  };

  function makeArrow(direction) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `${ARROW_CLASS} ${ARROW_CLASS}--${direction}`;
    button.setAttribute('aria-label', direction === 'prev' ? LABELS.prev : LABELS.next);
    button.innerHTML = CHEVRON;
    return button;
  }

  function update(controls) {
    const rail = controls.querySelector(RAIL_SELECTOR);
    const prev = controls.querySelector(`.${ARROW_CLASS}--prev`);
    const next = controls.querySelector(`.${ARROW_CLASS}--next`);
    if (!rail || !prev || !next) return;

    const { vertical, position, viewport, total } = metrics(rail);
    // 1px of slack absorbs sub-pixel rounding at the ends of the scroll range.
    const scrollable = total - viewport > 1;

    for (const arrow of [prev, next]) {
      arrow.hidden = !scrollable;
      arrow.classList.toggle(`${ARROW_CLASS}--vertical`, vertical);
    }

    prev.disabled = position <= 1;
    next.disabled = position >= total - viewport - 1;
  }

  function init(controls) {
    const rail = controls.querySelector(RAIL_SELECTOR);
    if (!rail) return;

    // Re-inject if a morph/re-render dropped the buttons but left the flag behind.
    if (controls.hasAttribute(INIT_ATTR) && controls.querySelector(`.${ARROW_CLASS}`)) {
      update(controls);
      return;
    }

    for (const stale of controls.querySelectorAll(`.${ARROW_CLASS}`)) stale.remove();

    const prev = makeArrow('prev');
    const next = makeArrow('next');

    const step = () => {
      const { viewport } = metrics(rail);
      // Just under a full viewport keeps a thumbnail visible across pages for context.
      return Math.max(viewport * 0.8, 44);
    };

    const scrollRail = (sign) => {
      const { vertical } = metrics(rail);
      const delta = sign * step();
      rail.scrollBy(vertical ? { top: delta, behavior: 'smooth' } : { left: delta, behavior: 'smooth' });
    };

    prev.addEventListener('click', () => scrollRail(-1));
    next.addEventListener('click', () => scrollRail(1));

    rail.before(prev);
    rail.after(next);

    const onUpdate = () => update(controls);
    rail.addEventListener('scroll', onUpdate, { passive: true });

    const observer = new ResizeObserver(onUpdate);
    observer.observe(rail);
    // Thumbnail images load lazily, so the rail's scroll extent grows after paint.
    for (const child of rail.children) observer.observe(child);

    controls.setAttribute(INIT_ATTR, 'true');
    onUpdate();
  }

  const initAll = (root = document) => {
    for (const controls of root.querySelectorAll(ROOT_SELECTOR)) init(controls);
    if (root instanceof Element && root.matches(ROOT_SELECTOR)) init(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  // The gallery is re-rendered on variant change; catch replaced nodes.
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(ROOT_SELECTOR) || node.querySelector(ROOT_SELECTOR)) {
          initAll(node);
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
